import {
  AccountLayout,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountInstruction,
  createCloseAccountInstruction,
  createInitializeAccountInstruction,
  createTransferInstruction,
  getAssociatedTokenAddress,
  MintLayout,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token0.3.8";
import type { Connection, Signer, TransactionInstruction } from "@solana/web3.js";
import { Keypair, PublicKey, SystemProgram, Transaction } from "@solana/web3.js";
import type { ParsedAccountBase } from "@stepfinance/common";
import { NATIVE_SOL, STEP_FEE_ADDRESS, WRAPPED_SOL_MINT } from "@stepfinance/common";
import { type NormalizedTokenAccount, type TokenAccount, TokenAccountParser } from "@stepfinance/tokens";
import BN from "bn.js";

import { TokenChangeNotificationDisplay } from "../components/Notifications/TokenChangeNotificationDisplay";
import { LAMPORTS_PER_SOL } from "../constants";
import { confirmOptions } from "../constants/transaction";
import type { ConnectedWallet } from "../hooks/wallet/useConnectedWallet";
import { AccountNotFoundError, cache, getCachedAccount } from "../utils/cache";
import { sendTransaction } from "../utils/transactions";

export async function wrapSol(
  connection: Connection,
  instructions: TransactionInstruction[],
  cleanupInstructions: TransactionInstruction[],
  signers: Signer[],
  payer: PublicKey,
  wrappedSolAccount: PublicKey,
  amount: BN,
): Promise<PublicKey> {
  const accountRentExempt = await connection.getMinimumBalanceForRentExemption(AccountLayout.span);

  const from = createWrappedAccount(
    instructions,
    cleanupInstructions,
    undefined,
    payer,
    amount.toNumber() + accountRentExempt,
    signers,
  );

  instructions.push(createTransferInstruction(from, wrappedSolAccount, payer, amount.toNumber(), [], TOKEN_PROGRAM_ID));

  return wrappedSolAccount;
}

export async function unwrapSol(
  connection: Connection,
  instructions: TransactionInstruction[],
  cleanupInstructions: TransactionInstruction[],
  signers: Signer[],
  payer: PublicKey,
  wrappedSolKey: PublicKey,
  wrappedSolAccount?: TokenAccount,
  amount?: BN,
) {
  if (!amount || !wrappedSolAccount || amount.eq(new BN(wrappedSolAccount.info.amount.toString()))) {
    instructions.push(createCloseAccountInstruction(wrappedSolKey, payer, payer, [], TOKEN_PROGRAM_ID));
  } else {
    const accountRentExempt = await connection.getMinimumBalanceForRentExemption(AccountLayout.span);

    const tempTo = createWrappedAccount(
      instructions,
      cleanupInstructions,
      wrappedSolAccount,
      payer,
      accountRentExempt,
      signers,
    );

    instructions.push(
      createTransferInstruction(wrappedSolAccount.pubkey, tempTo, payer, amount.toNumber(), [], TOKEN_PROGRAM_ID),
    );
  }
}

export function createWrappedAccount(
  instructions: TransactionInstruction[],
  cleanupInstructions: TransactionInstruction[],
  toCheck: TokenAccount | undefined,
  payer: PublicKey,
  amount: number,
  signers: Signer[],
) {
  if (toCheck && !toCheck.info.isNative) {
    return toCheck.pubkey;
  }

  const account = createUninitializedAccount(instructions, payer, amount, signers);

  instructions.push(createInitializeAccountInstruction(account, WRAPPED_SOL_MINT, payer, TOKEN_PROGRAM_ID));

  cleanupInstructions.push(createCloseAccountInstruction(account, payer, payer, [], TOKEN_PROGRAM_ID));

  return account;
}

export const DEFAULT_TEMP_MEM_SPACE = 65548;

export function createTempMemoryAccount(
  instructions: TransactionInstruction[],
  payer: PublicKey,
  signers: Signer[],
  owner: PublicKey,
  space = DEFAULT_TEMP_MEM_SPACE,
) {
  const account = new Keypair();
  instructions.push(
    SystemProgram.createAccount({
      fromPubkey: payer,
      newAccountPubkey: account.publicKey,
      // 0 will evict/close account since it cannot pay rent
      lamports: 0,
      space: space,
      programId: owner,
    }),
  );

  signers.push(account);

  return account.publicKey;
}

export function createUninitializedMint(
  instructions: TransactionInstruction[],
  payer: PublicKey,
  amount: number,
  signers: Keypair[],
) {
  const account = new Keypair();
  instructions.push(
    SystemProgram.createAccount({
      fromPubkey: payer,
      newAccountPubkey: account.publicKey,
      lamports: amount,
      space: MintLayout.span,
      programId: TOKEN_PROGRAM_ID,
    }),
  );

  signers.push(account);

  return account.publicKey;
}

export function createUninitializedAccount(
  instructions: TransactionInstruction[],
  payer: PublicKey,
  amount: number,
  signers: Signer[],
) {
  const account = new Keypair();
  instructions.push(
    SystemProgram.createAccount({
      fromPubkey: payer,
      newAccountPubkey: account.publicKey,
      lamports: amount,
      space: AccountLayout.span,
      programId: TOKEN_PROGRAM_ID,
    }),
  );

  signers.push(account);

  return account.publicKey;
}

export function createTokenAccount(
  instructions: TransactionInstruction[],
  payer: PublicKey,
  accountRentExempt: number,
  mint: PublicKey,
  owner: PublicKey,
  signers: Signer[],
) {
  const account = createUninitializedAccount(instructions, payer, accountRentExempt, signers);

  instructions.push(createInitializeAccountInstruction(account, mint, owner, TOKEN_PROGRAM_ID));

  return account;
}

export function createAssociatedTokenAccount(
  instructions: TransactionInstruction[],
  payer: PublicKey,
  associatedAddress: PublicKey,
  mint: PublicKey,
  owner: PublicKey,
) {
  instructions.push(
    createAssociatedTokenAccountInstruction(
      payer,
      associatedAddress,
      owner,
      mint,
      TOKEN_PROGRAM_ID,
      ASSOCIATED_TOKEN_PROGRAM_ID,
    ),
  );

  return associatedAddress;
}

export async function closeTokenAccounts(
  wallet: ConnectedWallet,
  connection: Connection,
  accounts: NormalizedTokenAccount[],
) {
  const rentPerAccount = await connection.getMinimumBalanceForRentExemption(AccountLayout.span);
  const tokenAccountList = accounts.map((account) => new PublicKey(account.pubkey));
  const transaction = await closeTokenAccountsTx(connection, wallet.publicKey, tokenAccountList);
  return await sendTransaction(connection, wallet, transaction.instructions, [], true, confirmOptions, true, {
    success: {
      message: "Your empty token account/s are closed",
      description: (
        <>
          <span>You reclaimed:</span>
          <TokenChangeNotificationDisplay
            mintAddress={NATIVE_SOL}
            amount={(rentPerAccount * accounts.length) / LAMPORTS_PER_SOL}
            type="increase"
          />
        </>
      ),
    },
  });
}

export async function closeTokenAccountsTx(connection: Connection, owner: PublicKey, tokenAccountKeyList: PublicKey[]) {
  const rentPerAccount = await connection.getMinimumBalanceForRentExemption(AccountLayout.span);
  const fee = Math.floor(rentPerAccount * tokenAccountKeyList.length * 0.05);

  const transaction = new Transaction();
  tokenAccountKeyList.forEach((account) => {
    transaction.add(createCloseAccountInstruction(account, owner, owner, [], TOKEN_PROGRAM_ID));
  });
  transaction.add(
    SystemProgram.transfer({
      fromPubkey: owner,
      toPubkey: STEP_FEE_ADDRESS,
      lamports: fee,
    }),
  );
  return transaction;
}

export async function findOrCreateAccountByMint(
  connection: Connection,
  payer: PublicKey,
  owner: PublicKey,
  instructions: TransactionInstruction[],
  mint: PublicKey, // use to identify same type
  excluded?: Set<string>,
  allowOwnerOffCurve?: boolean,
): Promise<PublicKey> {
  const account = await findAccountByMint(connection, owner, mint, excluded, allowOwnerOffCurve);
  if (account) {
    return account;
  }

  const associatedAddress = await getAssociatedTokenAddress(
    mint,
    owner,
    allowOwnerOffCurve,
    TOKEN_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );

  return createAssociatedTokenAccount(instructions, payer, associatedAddress, mint, owner);
}

export async function findAccountByMint(
  connection: Connection,
  owner: PublicKey,
  mint: PublicKey, // use to identify same type
  excluded?: Set<string>,
  allowOwnerOffCurve?: boolean,
): Promise<PublicKey | undefined> {
  const associatedAddress = await getAssociatedTokenAddress(
    mint,
    owner,
    allowOwnerOffCurve,
    TOKEN_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );

  let account: ParsedAccountBase | undefined;
  const accountToFind = associatedAddress;
  if (excluded === undefined || (excluded && !excluded.has(associatedAddress.toBase58()))) {
    try {
      account = await cache.query(connection, accountToFind, TokenAccountParser);
    } catch (e) {
      if (!(e instanceof AccountNotFoundError)) {
        throw e;
      }
    }
  }

  // If ATA doesn't exist find auxiliary account
  if (!account) {
    account = getCachedAccount(
      (acc) =>
        acc.info &&
        acc.info.mint &&
        acc.info.mint.toBase58() === mint.toBase58() &&
        acc.info.owner.toBase58() === owner.toBase58() &&
        (excluded === undefined || !excluded.has(acc.pubkey.toBase58())),
    );
  }

  if (account) {
    return account.pubkey;
  }

  return undefined;
}
